import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import styles from './MascotMotion.module.css';

export type MascotKind = 'thread' | 'basket' | 'guardian';
export type MascotMode = 'journey' | 'cta';

export interface MascotMotionProps {
  kind: MascotKind;
  width: number;
  height: number;
  className?: string | undefined;
  mode?: MascotMode;
  eager?: boolean;
}

const POINTER_STRENGTH: Record<MascotKind, number> = {
  thread: 1,
  basket: 0.7,
  guardian: 0.45,
};

/**
 * Lightweight motion wrapper for the approved WASHED mascot cutouts.
 *
 * The artwork itself never changes: pointer parallax, entrance, idle and tap
 * reactions are composed on separate transform layers so they cannot fight one
 * another. Entrance/idle CSS keys off an ancestor Reveal's `[data-in]` state,
 * keeping mascot choreography aligned with the site's existing motion system.
 */
export function MascotMotion({
  kind,
  width,
  height,
  className,
  mode = 'journey',
  eager = false,
}: MascotMotionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const reactionTimerRef = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);

  const resetPointerPose = () => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty('--mascot-x', '0px');
    root.style.setProperty('--mascot-y', '0px');
    root.style.setProperty('--mascot-r', '0deg');
  };

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = () => {
      reducedMotionRef.current = media.matches;
      if (!media.matches) return;
      const root = rootRef.current;
      root?.style.setProperty('--mascot-x', '0px');
      root?.style.setProperty('--mascot-y', '0px');
      root?.style.setProperty('--mascot-r', '0deg');
    };

    syncPreference();
    media.addEventListener('change', syncPreference);

    return () => {
      media.removeEventListener('change', syncPreference);
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
      if (reactionTimerRef.current != null) window.clearTimeout(reactionTimerRef.current);
    };
  }, []);

  // Idle loops run for minutes on a page; pause them while the mascot is off
  // screen so mid-range phones don't spend battery animating what nobody sees.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) root.removeAttribute('data-offscreen');
      else root.setAttribute('data-offscreen', '');
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || reducedMotionRef.current) return;

    const root = event.currentTarget;
    const rect = root.getBoundingClientRect();
    const clientX = event.clientX;
    const clientY = event.clientY;
    const strength = POINTER_STRENGTH[kind];

    if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
    frameRef.current = window.requestAnimationFrame(() => {
      const nx = Math.max(
        -1,
        Math.min(1, ((clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1),
      );
      const ny = Math.max(
        -1,
        Math.min(1, ((clientY - rect.top) / Math.max(rect.height, 1)) * 2 - 1),
      );

      root.style.setProperty('--mascot-x', `${(nx * 7 * strength).toFixed(2)}px`);
      root.style.setProperty('--mascot-y', `${(ny * 4 * strength).toFixed(2)}px`);
      root.style.setProperty('--mascot-r', `${(nx * 2.6 * strength).toFixed(2)}deg`);
      frameRef.current = null;
    });
  };

  const triggerReaction = () => {
    const root = rootRef.current;
    if (!root || reducedMotionRef.current) return;

    root.removeAttribute('data-reacting');
    // Force a style flush so repeated taps/clicks restart the short reaction.
    void root.offsetWidth;
    root.setAttribute('data-reacting', '');

    if (reactionTimerRef.current != null) window.clearTimeout(reactionTimerRef.current);
    reactionTimerRef.current = window.setTimeout(() => {
      root.removeAttribute('data-reacting');
      reactionTimerRef.current = null;
    }, 900);
  };

  const classes = [styles.root, className].filter(Boolean).join(' ');

  return (
    <div
      ref={rootRef}
      className={classes}
      data-kind={kind}
      data-mode={mode}
      aria-hidden="true"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointerPose}
      onPointerDown={triggerReaction}
    >
      <div className={styles.pose}>
        <div className={styles.entrance}>
          <div className={styles.reaction}>
            <img
              className={styles.image}
              src={`/mascots/${kind}.webp`}
              alt=""
              width={width}
              height={height}
              loading={eager ? 'eager' : 'lazy'}
              decoding="async"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
